import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  GraduationCap,
  Users,
  Brain,
  FileText,
  Upload,
  Calendar,
  CheckCircle,
  Shield,
  BarChart3,
  MessageSquare,
  ArrowRight,
  ArrowLeft,
  ChevronRight,
  Star,
  Zap,
  Globe,
  Clock,
} from "lucide-react";

const TOUR_STEPS = [
  {
    id: "hero",
    title: "AI-Powered RPL Assessment",
    subtitle: "Transform prior learning into recognised qualifications",
    description: "Credentia AI uses advanced artificial intelligence to assess candidates' existing skills, work experience, and qualifications against formal competency frameworks — awarding recognised credentials without repeating formal education.",
    icon: GraduationCap,
    color: "from-blue-600 to-indigo-700",
    features: [
      { icon: Brain, text: "GPT-5.2 AI Analysis Engine" },
      { icon: Shield, text: "Document Authentication" },
      { icon: Globe, text: "Live & Written Interviews" },
      { icon: Zap, text: "Automated Workflow" },
    ],
  },
  {
    id: "registration",
    title: "Step 1: Candidate Registration",
    subtitle: "Multiple entry channels for flexibility",
    description: "Candidates can join through 3 channels: Admin invitation via email, self-registration through the public portal, or Google sign-in. Each candidate receives login credentials and a guided onboarding experience.",
    icon: Users,
    color: "from-teal-500 to-emerald-600",
    mockup: {
      type: "flow",
      steps: [
        { label: "Invitation Email Sent", status: "done" },
        { label: "Candidate Logs In", status: "done" },
        { label: "Forced Password Change", status: "done" },
        { label: "Profile Completion (5 sections)", status: "active" },
        { label: "Document Upload Unlocked", status: "pending" },
      ],
    },
  },
  {
    id: "profile",
    title: "Step 2: Profile Completion",
    subtitle: "5 comprehensive sections for full context",
    description: "Before uploading documents, candidates complete their profile: Personal details, Current Employment, Training & Certifications, Work History, and a Declaration of Authenticity. This gives the AI full context for accurate assessment.",
    icon: FileText,
    color: "from-purple-500 to-violet-600",
    mockup: {
      type: "tabs",
      items: [
        { name: "Personal", desc: "Name, phone, nationality, address" },
        { name: "Employment", desc: "Current role, employer, responsibilities" },
        { name: "Training", desc: "Certificates, courses, qualifications" },
        { name: "Work History", desc: "Previous positions and duties" },
        { name: "Declaration", desc: "Authenticity confirmation" },
      ],
    },
  },
  {
    id: "upload",
    title: "Step 3: Document Upload",
    subtitle: "Evidence collection with smart categorisation",
    description: "Candidates upload their certificates, reference letters, project portfolios, and training records. The system performs instant authenticity checks — verifying names, dates, and content alignment. Documents support PDF, DOC, JPG, and more.",
    icon: Upload,
    color: "from-amber-500 to-orange-600",
    mockup: {
      type: "documents",
      items: [
        { name: "OSHA Safety Certificate.pdf", category: "Professional Certificates", status: "verified" },
        { name: "Employment Reference - Macada.pdf", category: "Employment Reference", status: "verified" },
        { name: "Project Portfolio 2023.pdf", category: "Project Documentation", status: "verified" },
        { name: "Training Records.docx", category: "Training Records", status: "checking" },
      ],
    },
  },
  {
    id: "ai-analysis",
    title: "Step 4: AI-Powered Analysis",
    subtitle: "GPT-5.2 maps evidence against competency frameworks",
    description: "The AI engine reads all documents and profile data, then assesses evidence against the selected competency framework. It generates personalised award recommendations, confidence scores, evidence gap reports, and document authentication checks.",
    icon: Brain,
    color: "from-violet-600 to-purple-700",
    mockup: {
      type: "analysis",
      result: {
        award: "Diploma in Construction Project Management",
        confidence: 82,
        coverage: "8/10 competencies evidenced",
        risk: "LOW",
      },
    },
  },
  {
    id: "interview",
    title: "Step 5: Interview Assessment",
    subtitle: "Two modes: Live or Written",
    description: "AI generates 10 personalised interview questions based on evidence gaps. Choose Live Interview (video call with industry specialist) or Written Interview (candidate answers in writing, assessor scores remotely). Includes up to 3 follow-up clarification rounds.",
    icon: Calendar,
    color: "from-green-500 to-emerald-600",
    mockup: {
      type: "comparison",
      options: [
        {
          title: "Live Interview",
          items: ["3 date options for candidate", "Video/in-person meeting", "Industry specialist conducts", "Real-time scoring"],
        },
        {
          title: "Written Interview",
          items: ["Candidate answers in writing", "No login needed (email link)", "Assessor scores remotely", "Up to 3 clarifications per Q"],
        },
      ],
    },
  },
  {
    id: "scoring",
    title: "Step 6: Assessment & Scoring",
    subtitle: "Multi-layer quality assurance",
    description: "Assessors score each competency (0-5 scale) based on AI findings and evidence. IQA verifiers conduct quality checks. The system tracks confidence calibration — comparing AI assessment with human scoring for continuous improvement.",
    icon: Star,
    color: "from-rose-500 to-pink-600",
    mockup: {
      type: "scoring",
      competencies: [
        { name: "Site Safety Management", ai: "Strong", assessor: 5 },
        { name: "Project Planning", ai: "Adequate", assessor: 4 },
        { name: "Team Leadership", ai: "Partial", assessor: 3 },
        { name: "Budget Management", ai: "Weak", assessor: 2 },
      ],
    },
  },
  {
    id: "completion",
    title: "Step 7: Award & Certification",
    subtitle: "Professional reports generated automatically",
    description: "Upon successful assessment, the system generates IPCB Evaluation Reports and Institution Competency Reports. Candidates see 100% progress with their awarded qualification. The entire journey — from registration to certification — is tracked and auditable.",
    icon: CheckCircle,
    color: "from-green-600 to-emerald-700",
    mockup: {
      type: "completion",
      award: "Diploma in Construction Project Management",
      credits: 120,
      reports: ["IPCB Evaluation Report", "Institution Competency Report"],
    },
  },
];

export default function DemoTour() {
  const [currentStep, setCurrentStep] = useState(0);
  const step = TOUR_STEPS[currentStep];
  const Icon = step.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white text-lg">Credentia AI</span>
          </Link>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-slate-300 border-slate-600">
              Product Tour
            </Badge>
            <Link to="/apply">
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                Apply Now
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2">
          {TOUR_STEPS.map((s, idx) => {
            const StepIcon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setCurrentStep(idx)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  idx === currentStep
                    ? "bg-white text-slate-900"
                    : idx < currentStep
                    ? "bg-green-500/20 text-green-400 border border-green-500/30"
                    : "bg-slate-800 text-slate-400 border border-slate-700"
                }`}
              >
                {idx < currentStep ? (
                  <CheckCircle className="w-3.5 h-3.5" />
                ) : (
                  <StepIcon className="w-3.5 h-3.5" />
                )}
                <span className="hidden sm:inline">{s.id === "hero" ? "Overview" : `Step ${idx}`}</span>
              </button>
            );
          })}
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left: Text Content */}
          <div className="space-y-6">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>

            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{step.title}</h1>
              <p className="text-lg text-blue-300">{step.subtitle}</p>
            </div>

            <p className="text-slate-300 leading-relaxed">{step.description}</p>

            {/* Hero features */}
            {step.features && (
              <div className="grid grid-cols-2 gap-3">
                {step.features.map((f, i) => {
                  const FIcon = f.icon;
                  return (
                    <div key={i} className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                      <FIcon className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-slate-200">{f.text}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center gap-3 pt-4">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
              )}
              {currentStep < TOUR_STEPS.length - 1 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className={`bg-gradient-to-r ${step.color} text-white`}
                >
                  Next Step <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Link to="/apply">
                  <Button className="bg-green-600 hover:bg-green-700 text-white">
                    Start Your Assessment <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Right: Visual Mockup */}
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6 backdrop-blur-sm">
            {/* Flow mockup */}
            {step.mockup?.type === "flow" && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-400 mb-4">Registration Flow</h3>
                {step.mockup.steps.map((s, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      s.status === "done" ? "bg-green-500 text-white" :
                      s.status === "active" ? "bg-blue-500 text-white animate-pulse" :
                      "bg-slate-700 text-slate-400"
                    }`}>
                      {s.status === "done" ? <CheckCircle className="w-4 h-4" /> : i + 1}
                    </div>
                    <span className={`text-sm ${
                      s.status === "done" ? "text-green-400" :
                      s.status === "active" ? "text-blue-300 font-medium" :
                      "text-slate-500"
                    }`}>{s.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Tabs mockup */}
            {step.mockup?.type === "tabs" && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-400 mb-4">Profile Sections</h3>
                {step.mockup.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <div className="w-7 h-7 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold">{i + 1}</div>
                    <div>
                      <p className="text-sm font-medium text-slate-200">{item.name}</p>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Documents mockup */}
            {step.mockup?.type === "documents" && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-400 mb-4">Uploaded Documents</h3>
                {step.mockup.items.map((doc, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <FileText className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-200 truncate">{doc.name}</p>
                      <p className="text-xs text-slate-500">{doc.category}</p>
                    </div>
                    <Badge className={doc.status === "verified" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"}>
                      {doc.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {/* AI Analysis mockup */}
            {step.mockup?.type === "analysis" && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-400 mb-4">AI Analysis Result</h3>
                <div className="p-4 bg-gradient-to-br from-violet-900/30 to-purple-900/30 rounded-lg border border-purple-500/30">
                  <p className="text-xs text-purple-300">Recommended Award</p>
                  <p className="text-lg font-bold text-white mt-1">{step.mockup.result.award}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <p className="text-xs text-slate-400">Confidence</p>
                    <p className="text-2xl font-bold text-blue-400">{step.mockup.result.confidence}%</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <p className="text-xs text-slate-400">Coverage</p>
                    <p className="text-lg font-bold text-green-400">{step.mockup.result.coverage}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-green-900/20 rounded-lg border border-green-500/20">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-300">Document Authentication: {step.mockup.result.risk} risk</span>
                </div>
              </div>
            )}

            {/* Comparison mockup */}
            {step.mockup?.type === "comparison" && (
              <div className="grid grid-cols-2 gap-3">
                {step.mockup.options.map((opt, i) => (
                  <div key={i} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <h4 className="text-sm font-semibold text-white mb-3">{opt.title}</h4>
                    <ul className="space-y-2">
                      {opt.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-xs text-slate-300">
                          <CheckCircle className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Scoring mockup */}
            {step.mockup?.type === "scoring" && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-400 mb-4">Competency Scoring</h3>
                {step.mockup.competencies.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-200">{c.name}</p>
                    </div>
                    <Badge className={
                      c.ai === "Strong" ? "bg-green-500/20 text-green-400" :
                      c.ai === "Adequate" ? "bg-blue-500/20 text-blue-400" :
                      c.ai === "Partial" ? "bg-amber-500/20 text-amber-400" :
                      "bg-red-500/20 text-red-400"
                    }>{c.ai}</Badge>
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center text-sm font-bold">
                      {c.assessor}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Completion mockup */}
            {step.mockup?.type === "completion" && (
              <div className="text-center space-y-4 py-4">
                <div className="w-20 h-20 rounded-full bg-green-500/20 mx-auto flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-green-400 uppercase tracking-wide">Awarded</p>
                  <p className="text-xl font-bold text-white mt-1">{step.mockup.award}</p>
                  <p className="text-sm text-slate-400 mt-1">{step.mockup.credits} Credits</p>
                </div>
                <div className="space-y-2 pt-4">
                  {step.mockup.reports.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-900/50 rounded-lg border border-slate-700/30">
                      <FileText className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-slate-300">{r}</span>
                      <Badge className="ml-auto bg-green-500/20 text-green-400 text-[10px]">PDF</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hero visual */}
            {!step.mockup && step.id === "hero" && (
              <div className="space-y-4 py-4">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/20 mx-auto flex items-center justify-center border border-blue-500/30">
                    <Brain className="w-12 h-12 text-blue-400" />
                  </div>
                  <p className="text-slate-300 mt-4 text-sm">Powered by GPT-5.2</p>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-4">
                  {[
                    { label: "Faster", value: "10x" },
                    { label: "Accuracy", value: "95%" },
                    { label: "Cost Saving", value: "60%" },
                  ].map((stat, i) => (
                    <div key={i} className="text-center p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                      <p className="text-xl font-bold text-blue-400">{stat.value}</p>
                      <p className="text-xs text-slate-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center p-8 bg-gradient-to-r from-blue-600/10 to-indigo-600/10 rounded-2xl border border-blue-500/20">
          <h2 className="text-2xl font-bold text-white mb-2">Ready to Transform Your RPL Process?</h2>
          <p className="text-slate-400 mb-6">Join institutions using AI to streamline recognition of prior learning.</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/apply">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Apply as Candidate
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
